/**
 * Menşei seçimi için ülke listesi.
 *
 * ISO 3166-1 alpha-2 kodu saklanır (veritabanında nvarchar(2)); ekranda Türkçe
 * ad ve bayrak gösterilir. Tam ISO listesi yerine ticaretle ilgili ülkeler
 * tutuldu — liste uzadıkça seçim zorlaşıyor. Eksik ülke çıkarsa buraya eklenir.
 */

export interface CountryOption {
  /** ISO 3166-1 alpha-2 */
  code: string;
  /** Türkçe ad */
  name: string;
}

export const TURKEY_CODE = 'TR';

export const COUNTRIES: CountryOption[] = [
  { code: 'TR', name: 'Türkiye' },
  { code: 'DE', name: 'Almanya' },
  { code: 'US', name: 'Amerika Birleşik Devletleri' },
  { code: 'AR', name: 'Arjantin' },
  { code: 'AL', name: 'Arnavutluk' },
  { code: 'AU', name: 'Avustralya' },
  { code: 'AT', name: 'Avusturya' },
  { code: 'AZ', name: 'Azerbaycan' },
  { code: 'BE', name: 'Belçika' },
  { code: 'BY', name: 'Belarus' },
  { code: 'AE', name: 'Birleşik Arap Emirlikleri' },
  { code: 'GB', name: 'Birleşik Krallık' },
  { code: 'BA', name: 'Bosna-Hersek' },
  { code: 'BR', name: 'Brezilya' },
  { code: 'BG', name: 'Bulgaristan' },
  { code: 'CZ', name: 'Çekya' },
  { code: 'CN', name: 'Çin' },
  { code: 'DK', name: 'Danimarka' },
  { code: 'ID', name: 'Endonezya' },
  { code: 'AM', name: 'Ermenistan' },
  { code: 'MA', name: 'Fas' },
  { code: 'FI', name: 'Finlandiya' },
  { code: 'FR', name: 'Fransa' },
  { code: 'ZA', name: 'Güney Afrika' },
  { code: 'KR', name: 'Güney Kore' },
  { code: 'GE', name: 'Gürcistan' },
  { code: 'IN', name: 'Hindistan' },
  { code: 'NL', name: 'Hollanda' },
  { code: 'HR', name: 'Hırvatistan' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'İran' },
  { code: 'IE', name: 'İrlanda' },
  { code: 'ES', name: 'İspanya' },
  { code: 'IL', name: 'İsrail' },
  { code: 'SE', name: 'İsveç' },
  { code: 'CH', name: 'İsviçre' },
  { code: 'IT', name: 'İtalya' },
  { code: 'JP', name: 'Japonya' },
  { code: 'ME', name: 'Karadağ' },
  { code: 'QA', name: 'Katar' },
  { code: 'KZ', name: 'Kazakistan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'CY', name: 'Kıbrıs' },
  { code: 'KG', name: 'Kırgızistan' },
  { code: 'CO', name: 'Kolombiya' },
  { code: 'XK', name: 'Kosova' },
  { code: 'KW', name: 'Kuveyt' },
  { code: 'MK', name: 'Kuzey Makedonya' },
  { code: 'LV', name: 'Letonya' },
  { code: 'LT', name: 'Litvanya' },
  { code: 'LB', name: 'Lübnan' },
  { code: 'LU', name: 'Lüksemburg' },
  { code: 'HU', name: 'Macaristan' },
  { code: 'MY', name: 'Malezya' },
  { code: 'MX', name: 'Meksika' },
  { code: 'EG', name: 'Mısır' },
  { code: 'MD', name: 'Moldova' },
  { code: 'NO', name: 'Norveç' },
  { code: 'UZ', name: 'Özbekistan' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Polonya' },
  { code: 'PT', name: 'Portekiz' },
  { code: 'RO', name: 'Romanya' },
  { code: 'RU', name: 'Rusya' },
  { code: 'RS', name: 'Sırbistan' },
  { code: 'SG', name: 'Singapur' },
  { code: 'SK', name: 'Slovakya' },
  { code: 'SI', name: 'Slovenya' },
  { code: 'SA', name: 'Suudi Arabistan' },
  { code: 'SY', name: 'Suriye' },
  { code: 'CL', name: 'Şili' },
  { code: 'TW', name: 'Tayvan' },
  { code: 'TH', name: 'Tayland' },
  { code: 'TN', name: 'Tunus' },
  { code: 'TM', name: 'Türkmenistan' },
  { code: 'UA', name: 'Ukrayna' },
  { code: 'OM', name: 'Umman' },
  { code: 'JO', name: 'Ürdün' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'NZ', name: 'Yeni Zelanda' },
  { code: 'GR', name: 'Yunanistan' },
];

const BY_CODE: Record<string, CountryOption> = COUNTRIES.reduce((acc, c) => {
  acc[c.code] = c;
  return acc;
}, {} as Record<string, CountryOption>);

/** ISO koddan ülke adı; bilinmeyen kod olduğu gibi döner */
export function getCountryName(code?: string | null): string {
  if (!code) return '';
  return BY_CODE[code.toUpperCase()]?.name ?? code;
}

/**
 * ISO 3166-1 alpha-2 kodundan emoji bayrak üretir.
 * Bölgesel gösterge sembolleri: 'A' → U+1F1E6
 */
export function countryCodeToFlag(code?: string | null): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  const first = upper.charCodeAt(0) - 65;
  const second = upper.charCodeAt(1) - 65;
  if (first < 0 || first > 25 || second < 0 || second > 25) return '';
  return String.fromCodePoint(0x1f1e6 + first, 0x1f1e6 + second);
}
